import { motion } from 'framer-motion';
import { useState } from 'react';
import { useNavigate } from 'react-router';

type ErrorAction = {
  label: string;
  onClick?: () => void;
  to?: string;
};

export function ErrorPageBase({
  code,
  title,
  description,
  action,
}: {
  code: string | number;
  title: string;
  description?: string;
  action?: ErrorAction;
}) {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  const handlePrimary = () => {
    if (action?.onClick) return action.onClick();
    if (action?.to) return navigate(action.to);
    return navigate('/');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-8 px-4">
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 20 }}
          className="space-y-4"
        >
          <h1 className="text-9xl font-bold text-primary">{code}</h1>
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <h2 className="text-2xl font-semibold text-foreground">{title}</h2>
          </motion.div>
          {description ? (
            <p className="text-muted-foreground max-w-md mx-auto">{description}</p>
          ) : null}
        </motion.div>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onHoverStart={() => setIsHovered(true)}
          onHoverEnd={() => setIsHovered(false)}
          onClick={handlePrimary}
          className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        >
          <motion.span
            animate={{ x: isHovered ? [0, 5, 0] : 0 }}
            transition={{ duration: 0.5, repeat: isHovered ? Infinity : 0 }}
          >
            {action?.label ?? 'Go back home'}
          </motion.span>
        </motion.button>
      </div>
    </div>
  );
}

export default function ErrorPage() {
  return (
    <ErrorPageBase
      code={500}
      title="Something went wrong"
      description="A temporary error occurred. Please try again."
      action={{ label: 'Reload', onClick: () => window.location.reload() }}
    />
  );
}
